import { AssistChip, Button, Stack, Text } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type {
  IApiGetAppUserResponseBody,
  IApiRemoveAppUserResponseBody,
} from '~/lib/api/modules/Apps';
import { UserAvatar } from '~/components/atoms/UserAvatar';
import { Table } from '~/components/molecules/Table';
import { getAppRoleLabel } from '~/components/organisms/fields/AppRoleSelector';
import { parseUserInfo } from '~/helpers/parseUserInfo';
import { useRemoveAppUserMutation } from '~/lib/api/modules/Apps/hooks/useRemoveAppUserMutation';
import { useAuthUser } from '~/lib/auth';

export type IAppUserListItemProps = {
  appId: string;
  organizationId?: string;
  user: IApiGetAppUserResponseBody;
};

const styles = stylex.create({
  me: {
    fontWeight: 'bold',
  },
});

export const AppUserListItem: React.FC<IAppUserListItemProps> = (props) => {
  const { appId, organizationId, user } = props;
  const userInfo = parseUserInfo(user);
  const { t } = useTranslation();
  const removeAppUserMutation = useRemoveAppUserMutation();
  const authUser = useAuthUser();

  const { fullName, emailAddress } = userInfo;
  const isMe = authUser?._id === user._id;

  const handleRemove = ():
    | Promise<IApiRemoveAppUserResponseBody>
    | undefined => {
    // eslint-disable-next-line no-alert
    const doDeletion = confirm(t('RemoveOrganizationUserForm.confirm'));

    if (doDeletion) {
      return removeAppUserMutation.mutateAsync({ appId, userId: user._id });
    }
  };

  return (
    <Table.Row>
      <Stack
        as={Table.BodyAction}
        horizontal
        gap={3}
        href={
          organizationId
            ? `/apps/${appId}/settings/users/${user._id}`
            : undefined
        }
        sx={isMe && styles.me}
      >
        <UserAvatar user={user} />
        <Stack gap={1}>
          <Text variant="title" size="sm" truncate>
            {fullName}
            {isMe ? ` (${t('common.you')})` : null}
          </Text>
          <Text variant="body" size="sm" dimmed>
            {emailAddress}
          </Text>
        </Stack>
      </Stack>

      <Stack as={Table.Body} horizontal gap={2}>
        {organizationId ? (
          user.roles?.map((role, roleIndex) => (
            <AssistChip
              key={roleIndex}
              label={getAppRoleLabel(role, t) ?? role}
            />
          ))
        ) : (
          <AssistChip label={t('common.owner')} />
        )}
      </Stack>

      <Stack as={Table.Body} horizontal justify="end">
        {organizationId && (
          <Button variant="text" onClick={handleRemove}>
            {t('common.remove')}
          </Button>
        )}
      </Stack>
    </Table.Row>
  );
};
